import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/feature/MovieCard";
import { movies, typeLabels } from "@/mocks/movies";
import { formatPlays, typeBadgeClass } from "@/utils/format";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";

export default function Detail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [activeEpisode, setActiveEpisode] = useState(1);
  const [copied, setCopied] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);

  const movie = useMemo(() => movies.find((m) => m.id === id), [id]);

  usePageMeta({
    title: movie ? `${movie.title} - 97视频` : "影视详情 - 97视频",
    description: movie
      ? `${movie.title}（${movie.year}）由${movie.director}执导，${movie.actors
          .slice(0, 3)
          .join("、")}主演。${movie.synopsis}`
      : "97视频影视详情页，查看影片基本信息、剧情简介与相关推荐。",
    keywords: movie
      ? `${movie.title},97视频,${movie.genres.join(",")},${movie.region}`
      : "97视频,影视详情",
  });

  useEffect(() => {
    setActiveEpisode(1);
    setCopied(false);
    setShowPlayer(false);
    window.scrollTo({ top: 0 });
  }, [id]);

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter(
        (m) =>
          m.id !== movie.id &&
          (m.type === movie.type ||
            m.director === movie.director ||
            m.genres.some((g) => movie.genres.includes(g))),
      )
      .slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="mx-auto flex max-w-[1400px] flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-film-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">
            没有找到该影视内容
          </h1>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-accent-600"
          >
            返回上一页
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  const favorite = isFavorite(movie.id);
  const episodeList = movie.episodeCount
    ? Array.from({ length: movie.episodeCount }, (_, i) => i + 1)
    : [];

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // ignore clipboard errors
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={movie.poster}
              alt=""
              aria-hidden="true"
              className="h-full w-full scale-110 object-cover object-top blur-2xl opacity-30"
            />
            <div className="absolute inset-0 bg-background-50/85"></div>
          </div>

          <div className="relative mx-auto max-w-[1400px] px-4 py-8 md:px-8 md:py-12">
            <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
              <Link to="/" className="cursor-pointer hover:text-accent-500">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <Link to={`/category/${movie.type}`} className="cursor-pointer hover:text-accent-500">
                {typeLabels[movie.type]}
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-foreground-800">{movie.title}</span>
            </nav>

            <div className="flex flex-col gap-8 md:flex-row">
              <div className="mx-auto w-48 shrink-0 overflow-hidden rounded-lg border border-background-200/70 md:mx-0 md:w-64">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 高清封面`}
                  title={`${movie.title} - 97视频`}
                  className="aspect-[2/3] w-full object-cover object-top"
                />
              </div>

              <div className="flex-1">
                <span className={`inline-block rounded-md px-2.5 py-1 text-xs font-medium ${typeBadgeClass(movie.type)}`}>
                  {typeLabels[movie.type]}
                </span>
                <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
                  {movie.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
                  <span className="flex items-center gap-1 text-accent-500">
                    <i className="ri-star-fill" aria-hidden="true"></i>
                    <strong className="text-base">{movie.rating.toFixed(1)}</strong>
                  </span>
                  <span>{movie.year}</span>
                  <span>{movie.region}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" aria-hidden="true"></i>
                    {formatPlays(movie.plays)}次播放
                  </span>
                  {movie.episodeCount && <span>共{movie.episodeCount}集</span>}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {movie.genres.map((genre) => (
                    <span
                      key={genre}
                      className="whitespace-nowrap rounded-full bg-secondary-500/15 px-3 py-1 text-xs font-medium text-secondary-700"
                    >
                      {genre}
                    </span>
                  ))}
                </div>

                <dl className="mt-5 space-y-2 text-sm">
                  <div className="flex gap-2">
                    <dt className="w-16 shrink-0 text-foreground-500">导演</dt>
                    <dd className="text-foreground-800">{movie.director}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-16 shrink-0 text-foreground-500">主演</dt>
                    <dd className="text-foreground-800">{movie.actors.join(" / ")}</dd>
                  </div>
                </dl>

                <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground-600">
                  {movie.synopsis}
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowPlayer(true)}
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                  >
                    <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                    立即播放（演示）
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleFavorite(movie.id)}
                    className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border px-5 py-3 text-sm font-medium transition-colors ${
                      favorite
                        ? "border-accent-500 bg-accent-500/10 text-accent-500"
                        : "border-background-300/70 text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    <i className={favorite ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true"></i>
                    {favorite ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-5 py-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
                  >
                    <i className="ri-share-forward-line text-lg" aria-hidden="true"></i>
                    {copied ? "已复制链接" : "分享"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {episodeList.length > 0 && (
          <section className="mx-auto max-w-[1400px] px-4 py-8 md:px-8">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
              剧集列表
            </h2>
            <div className="grid grid-cols-5 gap-2 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12">
              {episodeList.map((ep) => (
                <button
                  key={ep}
                  type="button"
                  onClick={() => setActiveEpisode(ep)}
                  className={`h-10 cursor-pointer rounded-md text-sm font-medium transition-colors ${
                    activeEpisode === ep
                      ? "bg-accent-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {ep}
                </button>
              ))}
            </div>
            <div className="mt-4 rounded-lg border border-background-200/70 bg-background-100 p-4 text-sm leading-relaxed text-foreground-600">
              第{activeEpisode}集：{movie.title}剧情持续推进，
              {movie.actors[0]}饰演的角色将面临新的抉择，更多精彩内容等你观看（演示简介）。
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="mx-auto max-w-[1400px] px-4 py-8 md:px-8">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
              相关推荐
            </h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-6">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </section>
        )}

        <div className="mx-auto max-w-[1400px] px-4 pb-10 md:px-8">
          <Link
            to={`/category/${movie.type}`}
            className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-foreground-600 hover:text-accent-500"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回{typeLabels[movie.type]}频道
          </Link>
        </div>

        {showPlayer && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
            onClick={() => setShowPlayer(false)}
          >
            <div
              className="w-full max-w-3xl overflow-hidden rounded-lg border border-background-300/60 bg-background-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-background-200/70 px-5 py-3">
                <h3 className="font-heading text-base font-bold text-foreground-950">
                  {movie.title}
                  {movie.episodeCount ? ` · 第${activeEpisode}集` : ""}
                </h3>
                <button
                  type="button"
                  aria-label="关闭"
                  onClick={() => setShowPlayer(false)}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-600 hover:bg-background-200"
                >
                  <i className="ri-close-line text-xl" aria-hidden="true"></i>
                </button>
              </div>
              <div className="flex aspect-video w-full flex-col items-center justify-center gap-4 bg-background-950/60 px-6 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className="ri-play-fill text-3xl" aria-hidden="true"></i>
                </span>
                <p className="text-sm text-foreground-700">
                  播放功能为前端演示状态，真实影视资源接入后即可在线观看
                </p>
                <p className="text-xs text-foreground-500">
                  {movie.title} · {movie.year} · {movie.region}
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}